import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import React, { forwardRef } from 'react'
import { TouchTarget } from './button'
import { Link } from './link'

/**
 * Badge color variants (A7Y-649).
 *
 * Semantic palette keys route through CSS custom properties defined in
 * `a7y-webapp/src/index.css` + `a7y-admin/src/index.css`:
 *
 *   - Severity family (red/amber/yellow/green) → `--color-sev-{critical|high|medium|low}`
 *   - Accent family (blue/indigo/violet/purple/zinc) → `--color-accent-{info|verdict|strategic}`
 *
 * Keys without a clean semantic home (orange, lime, emerald, teal, cyan, sky,
 * fuchsia, pink, rose) retain Tailwind utility class literals until a future
 * ticket maps them. The `BadgeColorName` union is intentionally unchanged so
 * existing call sites continue to compile untouched.
 *
 * Hover uses `color-mix(in oklch, <bg> 75%, transparent)` — a 25% transparent
 * overlay on the already-tinted background — and is wired to the
 * `group-data-hover` variant so `BadgeButton`'s Headless button drives it.
 */
export const badgeColors = {
  // Severity mappings
  red: 'bg-[var(--color-sev-critical-bg)] text-[var(--color-sev-critical)] ring-1 ring-inset ring-[var(--color-sev-critical-border)] group-data-hover:bg-[color-mix(in_oklch,var(--color-sev-critical-bg)_75%,transparent)]',
  amber:
    'bg-[var(--color-sev-high-bg)] text-[var(--color-sev-high)] ring-1 ring-inset ring-[var(--color-sev-high-border)] group-data-hover:bg-[color-mix(in_oklch,var(--color-sev-high-bg)_75%,transparent)]',
  yellow:
    'bg-[var(--color-sev-medium-bg)] text-[var(--color-sev-medium)] ring-1 ring-inset ring-[var(--color-sev-medium-border)] group-data-hover:bg-[color-mix(in_oklch,var(--color-sev-medium-bg)_75%,transparent)]',
  green:
    'bg-[var(--color-sev-low-bg)] text-[var(--color-sev-low)] ring-1 ring-inset ring-[var(--color-sev-low-border)] group-data-hover:bg-[color-mix(in_oklch,var(--color-sev-low-bg)_75%,transparent)]',
  // Accent mappings
  blue: 'bg-[var(--color-accent-info-bg)] text-[var(--color-accent-info)] ring-1 ring-inset ring-[var(--color-accent-info-border)] group-data-hover:bg-[color-mix(in_oklch,var(--color-accent-info-bg)_75%,transparent)]',
  indigo:
    'bg-[var(--color-accent-verdict-bg)] text-[var(--color-accent-verdict)] ring-1 ring-inset ring-[var(--color-accent-verdict-border)] group-data-hover:bg-[color-mix(in_oklch,var(--color-accent-verdict-bg)_75%,transparent)]',
  violet:
    'bg-[var(--color-accent-verdict-bg)] text-[var(--color-accent-verdict)] ring-1 ring-inset ring-[var(--color-accent-verdict-border)] group-data-hover:bg-[color-mix(in_oklch,var(--color-accent-verdict-bg)_75%,transparent)]',
  purple:
    'bg-[var(--color-accent-strategic-bg)] text-[var(--color-accent-strategic)] ring-1 ring-inset ring-[var(--color-accent-strategic-border)] group-data-hover:bg-[color-mix(in_oklch,var(--color-accent-strategic-bg)_75%,transparent)]',
  zinc: 'bg-[var(--color-accent-strategic-bg)] text-[var(--color-accent-strategic)] ring-1 ring-inset ring-[var(--color-accent-strategic-border)] group-data-hover:bg-[color-mix(in_oklch,var(--color-accent-strategic-bg)_75%,transparent)]',
  // Unmapped palette keys — retain Tailwind utility literals until a future ticket maps them.
  orange:
    'bg-orange-500/15 text-orange-700 group-data-hover:bg-orange-500/25 dark:bg-orange-500/10 dark:text-orange-400 dark:group-data-hover:bg-orange-500/20',
  lime: 'bg-lime-400/20 text-lime-700 group-data-hover:bg-lime-400/30 dark:bg-lime-400/10 dark:text-lime-300 dark:group-data-hover:bg-lime-400/15',
  emerald:
    'bg-emerald-500/15 text-emerald-700 group-data-hover:bg-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-400 dark:group-data-hover:bg-emerald-500/20',
  teal: 'bg-teal-500/15 text-teal-700 group-data-hover:bg-teal-500/25 dark:bg-teal-500/10 dark:text-teal-300 dark:group-data-hover:bg-teal-500/20',
  cyan: 'bg-cyan-400/20 text-cyan-700 group-data-hover:bg-cyan-400/30 dark:bg-cyan-400/10 dark:text-cyan-300 dark:group-data-hover:bg-cyan-400/15',
  sky: 'bg-sky-500/15 text-sky-700 group-data-hover:bg-sky-500/25 dark:bg-sky-500/10 dark:text-sky-300 dark:group-data-hover:bg-sky-500/20',
  fuchsia:
    'bg-fuchsia-400/15 text-fuchsia-700 group-data-hover:bg-fuchsia-400/25 dark:bg-fuchsia-400/10 dark:text-fuchsia-400 dark:group-data-hover:bg-fuchsia-400/20',
  pink: 'bg-pink-400/15 text-pink-700 group-data-hover:bg-pink-400/25 dark:bg-pink-400/10 dark:text-pink-400 dark:group-data-hover:bg-pink-400/20',
  rose: 'bg-rose-400/15 text-rose-700 group-data-hover:bg-rose-400/25 dark:bg-rose-400/10 dark:text-rose-400 dark:group-data-hover:bg-rose-400/20',
}

export type BadgeColorName = keyof typeof badgeColors

type BadgeProps = { color?: BadgeColorName }

export function Badge({ color = 'zinc', className, ...props }: BadgeProps & React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'inline-flex items-center gap-x-1.5 rounded-md px-1.5 py-0.5 text-sm/5 font-medium sm:text-xs/5 forced-colors:outline',
        badgeColors[color]
      )}
    />
  )
}

export const BadgeButton = forwardRef(function BadgeButton(
  {
    color = 'zinc',
    className,
    children,
    ...props
  }: BadgeProps & { className?: string; children: React.ReactNode } & (
      | ({ href?: never } & Omit<Headless.ButtonProps, 'as' | 'className'>)
      | ({ href: string } & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>)
    ),
  ref: React.ForwardedRef<HTMLElement>
) {
  let classes = clsx(
    className,
    'group relative inline-flex rounded-md focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500'
  )

  return typeof props.href === 'string' ? (
    <Link {...(props as React.ComponentPropsWithoutRef<typeof Link>)} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
      <TouchTarget>
        <Badge color={color}>{children}</Badge>
      </TouchTarget>
    </Link>
  ) : (
    <Headless.Button {...(props as Omit<Headless.ButtonProps, 'as' | 'className'>)} className={classes} ref={ref}>
      <TouchTarget>
        <Badge color={color}>{children}</Badge>
      </TouchTarget>
    </Headless.Button>
  )
})
